import { createReconnectBackoff, type ReconnectOptions } from '@runloop/reflex-client/reconnect';
import type { WsClientMessage, WsServerMessage } from '@reflex/shared';
import { getActiveOrgPreference, getAuthToken } from './api';

export type WsMessageHandler = (message: WsServerMessage) => void;
export type WsConnectionState = 'connecting' | 'open' | 'closed';
export type WsStateHandler = (state: WsConnectionState) => void;

/**
 * Cadence of the client's app-level `{ type: 'ping' }` probes. Each ping
 * earns a `{ type: 'pong' }` reply, so a healthy connection always has
 * app-visible traffic at least once per interval — even when no events
 * are flowing (e.g. the long event-less stretches of devbox provisioning).
 */
const HEARTBEAT_INTERVAL_MS = 25_000;

/**
 * How long the connection may go without ANY server message (events,
 * pongs, acks) before it is declared half-open and rebuilt. A socket
 * dropped without a close frame keeps `readyState` OPEN indefinitely
 * while sends buffer into the void, so silence is the only detectable
 * signal. Three missed heartbeats is conservative enough to survive
 * background-tab timer throttling without spurious reconnects.
 */
const STALE_CONNECTION_THRESHOLD_MS = 3 * HEARTBEAT_INTERVAL_MS;

/**
 * How long a liveness probe sent when the tab becomes visible may go
 * unanswered before the connection is rebuilt. A hidden tab has its timers
 * throttled (and a frozen tab has them stopped), so the heartbeat above
 * cannot vouch for a socket that was backgrounded: the tab may return to
 * the foreground holding a socket that died minutes ago, with the next
 * watchdog tick up to {@link HEARTBEAT_INTERVAL_MS} away. Probing on
 * refocus and giving the pong a short grace window turns that into a
 * sub-second detection instead.
 */
const VISIBILITY_PROBE_TIMEOUT_MS = 5_000;

/**
 * Minimum hidden stretch that earns a resync on refocus. Alt-tab flicker
 * cannot outrun the socket (nothing is throttled yet at this scale), and each
 * resync costs one full-transcript GET per mounted stream, so brief switches
 * are not worth refetching for. A closed socket is still reconnected
 * immediately regardless of how long the tab was away.
 */
const VISIBILITY_RESYNC_MIN_HIDDEN_MS = 1_000;

export class SocketManager {
  private ws: WebSocket | null = null;
  private url: string;
  private messageHandlers = new Set<WsMessageHandler>();
  private stateHandlers = new Set<WsStateHandler>();
  private activeSubscriptions = new Set<string>();
  /**
   * Per-stream replay cursor for paged subscriptions; see {@link subscribe}.
   * Last-writer-wins with no refcount: one subscribing surface per stream
   * is the assumption (a second, cursor-less subscriber would clobber the
   * paged cursor — the same dual-surface case the Set-based unsubscribe
   * above already does not support).
   */
  private subscriptionCursors = new Map<string, number>();
  private usageSubscribed = false;
  /**
   * Agents the local client is watching for live sandbox metrics. Tracked
   * here so each connect / reconnect replays the watches without the
   * panel having to re-mount. Mirrors `activeSubscriptions`.
   */
  private sandboxWatched = new Set<string>();
  private readonly reconnectBackoff: ReturnType<typeof createReconnectBackoff>;
  // State observers may close or replace an attempt before its socket exists.
  private connectionGeneration = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionallyClosed = false;
  private _state: WsConnectionState = 'closed';
  // State transitions, unlike connection disposal, supersede observer notifications.
  private stateRevision = 0;
  private hasConnectedBefore = false;
  /** A meaningful refocus replaced the initial handshake before it opened. */
  private resyncOnNextOpen = false;
  // Multiple hooks register resync callbacks (agent stream, axon viewer, flow runs).
  // Using a Set so concurrent mounts each get their own backfill.
  private reconnectCallbacks = new Set<() => void>();
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  /** Timestamp of the last server message — the liveness watchdog's input. */
  private lastServerActivity = 0;
  /**
   * Server messages seen on the current connection. The refocus probe waits
   * for this to move rather than for `lastServerActivity` to advance, since a
   * pong can land in the same millisecond the ping was sent.
   */
  private serverMessageCount = 0;
  private visibilityListener: (() => void) | null = null;
  /** When the tab went to the background, or null while it is visible. */
  private hiddenSince: number | null = null;
  private visibilityProbeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(url: string, options: ReconnectOptions = {}) {
    this.url = url;
    this.reconnectBackoff = createReconnectBackoff(options);
  }

  get state(): WsConnectionState {
    return this._state;
  }

  get endpoint(): string {
    return this.url;
  }

  connect(): void {
    if (!this.ws && this._state === 'connecting') return;
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.clearReconnectTimer();
    const generation = ++this.connectionGeneration;
    this.intentionallyClosed = false;
    this.startVisibilityWatch();
    this.setState('connecting');
    if (this.connectionGeneration !== generation) return;

    // Build the WS URL at connect time so each reconnect picks up the
    // current active-org preference. Browsers can't send custom headers
    // on a WS handshake, so we pass the org as a query param — the
    // server's `auth-middleware.ts#readActiveOrgId` accepts either an id
    // or a slug here. Without this the connection would bind to whatever
    // the `active_org` cookie said at upgrade time (or the user's first
    // membership), and broadcasts for the URL's org would be filtered
    // out by `WsBroadcastService.matchesOrg`.
    const token = getAuthToken();
    const orgId = getActiveOrgPreference();
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    if (orgId) params.set('organizationId', orgId);
    const query = params.toString();
    const wsUrl = query ? `${this.url}${this.url.includes('?') ? '&' : '?'}${query}` : this.url;
    // Every handler is guarded by a `this.ws === socket` identity check:
    // only the current connection may mutate manager state. `close()`
    // completes asynchronously, so a superseded socket (manual
    // `reconnect()`, org switch) delivers its remaining events after the
    // replacement exists — those are ignored.
    let socket: WebSocket;
    try {
      socket = new WebSocket(wsUrl);
    } catch (error) {
      // A malformed URL or injected constructor can fail before onclose exists.
      // Leave explicit connect retryable rather than stuck in the setup guard.
      if (this.connectionGeneration === generation) this.setState('closed');
      throw error;
    }
    this.ws = socket;

    socket.onopen = () => {
      if (this.ws !== socket) return;
      const shouldResync = this.hasConnectedBefore || this.resyncOnNextOpen;
      this.hasConnectedBefore = true;
      this.resyncOnNextOpen = false;
      this.reconnectBackoff.reset();
      this.lastServerActivity = Date.now();
      this.setState('open');
      if (this.connectionGeneration !== generation || this.ws !== socket) return;
      this.startHeartbeat();
      this.resubscribeAll(shouldResync);
    };

    socket.onmessage = (event: MessageEvent) => {
      if (this.ws !== socket) return;
      this.lastServerActivity = Date.now();
      this.serverMessageCount++;
      let parsed: unknown;
      try {
        parsed = JSON.parse(String(event.data));
      } catch {
        return;
      }
      this.advanceCursor(parsed as WsServerMessage);
      for (const handler of this.messageHandlers) {
        try {
          handler(parsed as WsServerMessage);
        } catch (err) {
          console.error('[SocketManager] message handler threw:', err);
        }
      }
    };

    socket.onclose = () => {
      if (this.ws !== socket) return;
      this.ws = null;
      this.stopHeartbeat();
      this.clearVisibilityProbe();
      this.setState('closed');

      if (this.connectionGeneration === generation && !this.intentionallyClosed) {
        this.scheduleReconnect();
      }
    };

    socket.onerror = () => {
      // onclose will fire after onerror, which handles reconnection
    };
  }

  /**
   * Start the liveness loop for the current connection. Every interval:
   * declare the connection dead and rebuild it when nothing app-visible
   * has arrived for {@link STALE_CONNECTION_THRESHOLD_MS} (the reconnect
   * path re-subscribes every stream and fires the reconnect callbacks,
   * which REST-backfill anything missed); otherwise send an app-level
   * ping so the server's pong keeps `lastServerActivity` fresh on a
   * healthy-but-quiet connection.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState !== WebSocket.OPEN) return;
      if (Date.now() - this.lastServerActivity > STALE_CONNECTION_THRESHOLD_MS) {
        this.reconnect();
        return;
      }
      this.sendClientMessage({ type: 'ping' });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Watch for the tab returning to the foreground. Registered once per
   * manager on the first `connect()` and torn down by `disconnect()`.
   */
  private startVisibilityWatch(): void {
    if (this.visibilityListener || typeof document === 'undefined') return;
    const listener = () => {
      if (document.visibilityState !== 'visible') {
        this.hiddenSince = Date.now();
        return;
      }
      const hiddenFor = this.hiddenSince === null ? 0 : Date.now() - this.hiddenSince;
      this.hiddenSince = null;
      this.handleVisible(hiddenFor);
    };
    // A tab can be hidden before it ever connects (link opened in the
    // background), in which case no hidden transition is observed and the first
    // refocus would look like a zero-length switch and skip its resync.
    if (document.visibilityState === 'hidden') this.hiddenSince = Date.now();
    this.visibilityListener = listener;
    document.addEventListener('visibilitychange', listener);
  }

  private stopVisibilityWatch(): void {
    if (this.visibilityListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityListener);
    }
    this.visibilityListener = null;
    this.clearVisibilityProbe();
  }

  /**
   * Bring the client back in sync after the tab was in the background.
   *
   * Nothing the client derives from the live socket can be trusted across a
   * hidden stretch: broadcasts may have been missed outright, and the
   * heartbeat that would normally notice was throttled or frozen along with
   * the rest of the page's timers. So on every refocus we
   *
   * - reconnect immediately when the socket is closed, skipping whatever is
   *   left of the reconnect backoff (which may have grown to
   *   the 30-second default maximum while nobody was watching);
   * - replace a handshake that remained CONNECTING across a meaningful hidden
   *   stretch, then resync once its replacement opens;
   * - run the resync callbacks so consumers REST-backfill their streams — the
   *   only way a transcript recovers an event that was dropped while hidden,
   *   and what keeps a shell command that finished in the background from
   *   still rendering as running;
   * - probe the socket and rebuild it if the pong does not come back, so a
   *   silently dead connection is replaced now instead of on the next
   *   watchdog tick.
   */
  private handleVisible(hiddenForMs: number): void {
    if (this.intentionallyClosed) return;

    if (this.ws?.readyState === WebSocket.CONNECTING) {
      if (hiddenForMs >= VISIBILITY_RESYNC_MIN_HIDDEN_MS) {
        this.resyncOnNextOpen = true;
        this.reconnect();
      }
      return;
    }

    if (this.ws?.readyState !== WebSocket.OPEN) {
      this.clearReconnectTimer();
      this.reconnectBackoff.reset();
      this.connect();
      return;
    }

    if (hiddenForMs < VISIBILITY_RESYNC_MIN_HIDDEN_MS) return;

    const generation = this.connectionGeneration;
    this.runResyncCallbacks();
    if (this.connectionGeneration !== generation) return;
    // Restart the heartbeat so its cadence is measured from now rather than
    // from a tick that may have been throttled mid-interval.
    this.startHeartbeat();
    this.probeLiveness();
  }

  private probeLiveness(): void {
    this.clearVisibilityProbe();
    const socket = this.ws;
    const messagesBeforeProbe = this.serverMessageCount;
    this.sendClientMessage({ type: 'ping' });
    this.visibilityProbeTimer = setTimeout(() => {
      this.visibilityProbeTimer = null;
      if (this.intentionallyClosed) return;
      // A different socket means the connection was already rebuilt (org
      // switch, watchdog, onclose backoff) and this probe is moot.
      if (this.ws !== socket) return;
      if (this.serverMessageCount > messagesBeforeProbe) return;
      this.reconnect();
    }, VISIBILITY_PROBE_TIMEOUT_MS);
  }

  private clearVisibilityProbe(): void {
    if (this.visibilityProbeTimer) {
      clearTimeout(this.visibilityProbeTimer);
      this.visibilityProbeTimer = null;
    }
  }

  disconnect(): void {
    ++this.connectionGeneration;
    this.intentionallyClosed = true;
    this.resyncOnNextOpen = false;
    this.clearReconnectTimer();
    this.stopHeartbeat();
    this.stopVisibilityWatch();
    const socket = this.ws;
    this.ws = null;
    socket?.close();
    this.setState('closed');
  }

  /**
   * Close the current connection (if any) and immediately open a new one.
   * Called when the active organization changes so the new handshake
   * picks up the new `?organizationId=` and the server rebinds the
   * connection's `WsConnectionMeta.organizationId`. Resets backoff so
   * an org switch always reconnects immediately.
   */
  reconnect(): void {
    const generation = ++this.connectionGeneration;
    this.clearVisibilityProbe();
    this.clearReconnectTimer();
    this.stopHeartbeat();
    const socket = this.ws;
    this.ws = null;
    socket?.close();
    this.reconnectBackoff.reset();
    this.setState('closed');
    if (this.connectionGeneration === generation) this.connect();
  }

  /**
   * Advance a stream's replay cursor as live events arrive, so a reconnect
   * resubscribes from the last event this client actually saw instead of
   * the cursor the surface set at page open. Only streams that already
   * carry a cursor advance — a cursor-less (legacy full-history)
   * subscription keeps its full-replay reconnect semantics, and the
   * single-surface-per-stream assumption on {@link subscriptionCursors}
   * holds because this only ever moves an existing entry forward.
   */
  private advanceCursor(message: WsServerMessage): void {
    if (message.type !== 'event') return;
    const { streamId, sequence } = message.event;
    if (typeof sequence !== 'number') return;
    const cursor = this.subscriptionCursors.get(streamId);
    if (cursor !== undefined && sequence > cursor) {
      this.subscriptionCursors.set(streamId, sequence);
    }
  }

  /**
   * `afterSequence` makes the server replay only history above that
   * sequence — a paged client that already fetched its window subscribes
   * at the window head so the socket is effectively live-only. Remembered
   * per stream so reconnect resubscribes carry it too.
   */
  subscribe(streamId: string, afterSequence?: number): void {
    this.activeSubscriptions.add(streamId);
    if (afterSequence === undefined) this.subscriptionCursors.delete(streamId);
    else this.subscriptionCursors.set(streamId, afterSequence);
    this.sendClientMessage(this.subscribeMessage(streamId));
  }

  unsubscribe(streamId: string): void {
    this.activeSubscriptions.delete(streamId);
    this.subscriptionCursors.delete(streamId);
    this.sendClientMessage({ type: 'unsubscribe', streamId });
  }

  private subscribeMessage(streamId: string): WsClientMessage {
    const afterSequence = this.subscriptionCursors.get(streamId);
    return {
      type: 'subscribe',
      streamId,
      ...(afterSequence !== undefined && { afterSequence }),
    };
  }

  /**
   * Opt the connection into the admin usage snapshot stream. The server gates
   * this on the user's permissions; failures arrive as a generic
   * `{ type: 'error' }` message.
   */
  subscribeToUsage(): void {
    this.usageSubscribed = true;
    this.sendClientMessage({ type: 'usage:subscribe' });
  }

  unsubscribeFromUsage(): void {
    this.usageSubscribed = false;
    this.sendClientMessage({ type: 'usage:unsubscribe' });
  }

  /**
   * Open the monitoring panel for `agentId`. Tells the server to start
   * pulling live metrics from the on-box bridge (viewer-gated). Safe to
   * call multiple times for the same agent — we de-dupe.
   */
  watchSandbox(agentId: string): void {
    if (this.sandboxWatched.has(agentId)) return;
    this.sandboxWatched.add(agentId);
    this.sendClientMessage({ type: 'sandbox:watch', agentId });
  }

  /** Close the panel. No-op when not watching. */
  unwatchSandbox(agentId: string): void {
    if (!this.sandboxWatched.delete(agentId)) return;
    this.sendClientMessage({ type: 'sandbox:unwatch', agentId });
  }

  getActiveSubscriptions(): ReadonlySet<string> {
    return this.activeSubscriptions;
  }

  onMessage(handler: WsMessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  /**
   * Test/Storybook seam: fan a server message out to every registered handler
   * as if it arrived over the wire. Unit tests and Storybook have no live WS,
   * so this is the only way to drive message-driven UI in those environments.
   */
  deliverTestMessage(message: WsServerMessage): void {
    for (const handler of this.messageHandlers) {
      try {
        handler(message);
      } catch (err) {
        console.error('[SocketManager] test message handler threw:', err);
      }
    }
  }

  /**
   * Observe state transitions synchronously; registration does not emit the current state.
   * Same-state updates are ignored. A nested transition supersedes remaining
   * notifications for the older transition. Returns an unregister function.
   */
  onStateChange(handler: WsStateHandler): () => void {
    this.stateHandlers.add(handler);
    return () => this.stateHandlers.delete(handler);
  }

  /**
   * Register a resync callback: a consumer's "reconcile whatever I may have
   * missed" hook, typically a REST backfill merged into a query cache. Fired
   * on every reconnect and whenever the tab returns to the foreground (see
   * {@link handleVisible}), so it must be idempotent.
   */
  addReconnectCallback(callback: () => void): void {
    this.reconnectCallbacks.add(callback);
  }

  removeReconnectCallback(callback: () => void): void {
    this.reconnectCallbacks.delete(callback);
  }

  private sendClientMessage(message: WsClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private resubscribeAll(shouldResync: boolean): void {
    for (const streamId of this.activeSubscriptions) {
      this.sendClientMessage(this.subscribeMessage(streamId));
    }
    if (this.usageSubscribed) {
      this.sendClientMessage({ type: 'usage:subscribe' });
    }
    for (const agentId of this.sandboxWatched) {
      this.sendClientMessage({ type: 'sandbox:watch', agentId });
    }
    if (shouldResync) {
      this.runResyncCallbacks();
    }
  }

  private runResyncCallbacks(): void {
    for (const cb of this.reconnectCallbacks) {
      try {
        cb();
      } catch (err) {
        console.error('[SocketManager] resync callback threw:', err);
      }
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null || this.intentionallyClosed || this.ws) return;
    const generation = this.connectionGeneration;
    this.reconnectTimer = setTimeout(() => {
      if (this.connectionGeneration !== generation || this.intentionallyClosed) return;
      this.reconnectTimer = null;
      this.connect();
    }, this.reconnectBackoff.nextDelayMs());
  }

  private setState(state: WsConnectionState): void {
    if (this._state === state) return;
    this._state = state;
    const revision = ++this.stateRevision;
    for (const handler of this.stateHandlers) {
      if (this.stateRevision !== revision) break;
      try {
        handler(state);
      } catch (err) {
        console.error('[SocketManager] state handler threw:', err);
      }
    }
  }
}

let instance: SocketManager | null = null;

export function getSocketManager(): SocketManager {
  if (!instance) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const base = import.meta.env.VITE_API_BASE_URL || '';
    let url: string;
    if (base.startsWith('http')) {
      url = base.replace(/^http/, 'ws') + '/ws';
    } else {
      url = `${protocol}//${window.location.host}/api/ws`;
    }
    instance = new SocketManager(url);
  }
  return instance;
}
